import { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, Loader2, X, Fan, Lightbulb, Wind, Video, Bot, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@ui/lib/utils';
import type { ControlStatus } from '../_shared/types';

export interface ControlCommand {
  id: string;
  target: string;
  label: string;
  value: string;
  status: ControlStatus;
  timestamp: number;
}

const TARGET_ICON: Record<string, typeof Fan> = { fan: Fan, light: Lightbulb, airRefresher: Wind, stream: Video, autoMode: Bot };

const STATUS_CONFIG: Record<ControlStatus, { icon: typeof Loader2; color: string; text: string }> = {
  PENDING: { icon: Loader2, color: 'text-blue-500', text: '전송 중' },
  PUBLISHED: { icon: Loader2, color: 'text-blue-500', text: '전송됨' },
  SUCCESS: { icon: CheckCircle2, color: 'text-green-500', text: '성공' },
  FAILED: { icon: XCircle, color: 'text-red-500', text: '실패' },
  TIMEOUT: { icon: XCircle, color: 'text-amber-500', text: '타임아웃' },
};

// ── 글로벌 커맨드 큐 ──
let commands: ControlCommand[] = [];
let listeners: Array<() => void> = [];
const TIMEOUT_MS = 60_000;

function notify() { listeners.forEach((fn) => fn()); }

/** 제어 요청 추가 — PENDING 상태로만 등록, SSE 결과를 기다림 */
export function addControlCommand(cmd: Omit<ControlCommand, 'id' | 'timestamp' | 'status'>) {
  const newCmd: ControlCommand = {
    ...cmd,
    id: `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    status: 'PENDING',
    timestamp: Date.now(),
  };
  commands = [newCmd, ...commands].slice(0, 20);
  notify();
  return newCmd.id;
}

/** SSE 결과 수신 시 상태 업데이트 */
export function updateControlCommand(id: string, status: ControlStatus) {
  commands = commands.map((c) => c.id === id ? { ...c, status } : c);
  notify();
}

/** 타임아웃 체크 — PENDING/PUBLISHED 상태가 60초 이상이면 TIMEOUT 처리 */
function checkTimeouts() {
  const now = Date.now();
  let changed = false;
  commands = commands.map((c) => {
    if ((c.status === 'PENDING' || c.status === 'PUBLISHED') && now - c.timestamp > TIMEOUT_MS) {
      changed = true;
      return { ...c, status: 'TIMEOUT' as ControlStatus };
    }
    return c;
  });
  if (changed) notify();
}

export function ControlStatusPanel() {
  const [, forceUpdate] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    listeners.push(listener);
    return () => { listeners = listeners.filter((l) => l !== listener); };
  }, []);

  // 타임아웃 체크 (10초마다)
  useEffect(() => {
    const interval = setInterval(checkTimeouts, 10_000);
    return () => clearInterval(interval);
  }, []);

  // 새 커맨드 추가 시 visible로 복원
  const prevLength = useState(commands.length)[0];
  useEffect(() => {
    if (commands.length > prevLength) setVisible(true);
  }, [commands.length, prevLength]);

  // 모든 커맨드가 완료되고 5초 후 자동 숨김
  useEffect(() => {
    const allDone = commands.length > 0 && commands.every((c) => c.status === 'SUCCESS' || c.status === 'FAILED' || c.status === 'TIMEOUT');
    if (allDone) {
      const timer = setTimeout(() => setVisible(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [commands.map((c) => c.status).join(',')]);

  if (commands.length === 0 || !visible) return null;

  const pending = commands.filter((c) => c.status === 'PENDING' || c.status === 'PUBLISHED').length;
  const success = commands.filter((c) => c.status === 'SUCCESS').length;
  const failed = commands.filter((c) => c.status === 'FAILED' || c.status === 'TIMEOUT').length;
  const total = commands.length;

  const handleDismiss = () => {
    setVisible(false);
    if (pending === 0) {
      commands = [];
      notify();
    }
  };

  return (
    <div className="pointer-events-none fixed left-0 right-0 z-[45] px-4" style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom))' }}>
      <div className="pointer-events-auto mx-auto max-w-lg overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
        {/* Header pill */}
        <div className="flex w-full items-center justify-between gap-2 px-4 py-2.5">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            {pending > 0 ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            ) : failed > 0 ? (
              <XCircle className="h-4 w-4 text-red-500" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
            <span className="truncate text-xs font-semibold text-neutral-700 dark:text-neutral-200">
              {pending > 0 ? `제어 ${total}건 진행 중` : `제어 ${total}건 완료`}
            </span>
            <div className="ml-2 flex shrink-0 items-center gap-1.5">
              {success > 0 && <span className="rounded-md bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-600 dark:bg-green-900 dark:text-green-400">성공 {success}</span>}
              {failed > 0 && <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600 dark:bg-red-900 dark:text-red-400">실패 {failed}</span>}
              {pending > 0 && <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-600 dark:bg-blue-900 dark:text-blue-400">대기 {pending}</span>}
            </div>
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => setExpanded(!expanded)} className="rounded-md p-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
              {expanded ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronUp className="h-4 w-4 text-neutral-400" />}
            </button>
            <button onClick={handleDismiss} className="rounded-md p-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
              <X className="h-3.5 w-3.5 text-neutral-400" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {pending > 0 && (
          <div className="px-4 pb-2">
            <div className="h-1 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-500"
                style={{ width: `${Math.round(((success + failed) / total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Expanded item list */}
        {expanded && (
          <div className="max-h-[min(12rem,35dvh)] overflow-y-auto border-t border-neutral-100 px-4 py-2 dark:border-neutral-800">
            {commands.map((cmd) => {
              const TargetIcon = TARGET_ICON[cmd.target] ?? Fan;
              const statusCfg = STATUS_CONFIG[cmd.status];
              const StatusIcon = statusCfg.icon;
              const isSpinning = cmd.status === 'PENDING' || cmd.status === 'PUBLISHED';

              return (
                <div key={cmd.id} className="flex items-center gap-3 py-1.5">
                  <TargetIcon className="h-4 w-4 text-neutral-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                      {cmd.label}
                    </span>
                    <span className="ml-1.5 text-xs text-neutral-400">{cmd.value}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <StatusIcon className={cn('h-3.5 w-3.5', statusCfg.color, isSpinning && 'animate-spin')} />
                    <span className={cn('text-[10px] font-semibold', statusCfg.color)}>{statusCfg.text}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
